import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useOrgFleet } from "providers/OrgFleetProvider";
import SingleARCProvider from "./SingleARCProvider";
import { setArcId, setArcProxyHost } from "store/arcProxySlice";
import { api as missionStorageApi } from "app/services/missionStorageApi";

const FleetARCProvider = ({ children }: { children?: React.ReactNode }) => {
    const { arcId } = useParams<{ arcId: string }>();
    const { proxyHostname } = useOrgFleet();
    const dispatch = useDispatch();

    const fleetConnectInfo = useMemo(
        () =>
            arcId && proxyHostname
                ? { arcId, proxyPublicHostname: proxyHostname, isSecure: true }
                : null,
        [arcId, proxyHostname]
    );
    const [syncedConnection, setSyncedConnection] = useState<typeof fleetConnectInfo>(null);

    useEffect(() => {
        if (!fleetConnectInfo) return;
        dispatch(setArcId(fleetConnectInfo.arcId));
        dispatch(setArcProxyHost(fleetConnectInfo.proxyPublicHostname));
        // Query arguments do not include ARC identity, so cached data cannot
        // be reused when changing the proxy destination.
        dispatch(missionStorageApi.util.resetApiState());
        setSyncedConnection(fleetConnectInfo);
        return () => {
            dispatch(setArcId(null));
            dispatch(setArcProxyHost(null));
            setSyncedConnection(null);
        };
    }, [fleetConnectInfo, dispatch]);

    if (!fleetConnectInfo || syncedConnection !== fleetConnectInfo) return null;

    return (
        <SingleARCProvider key={arcId} fleetConnectInfo={fleetConnectInfo}>
            {children}
        </SingleARCProvider>
    );
};

export default FleetARCProvider;
